import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/atoms';
import ConditionalWarningPopupButton from 'components/molecules/buttons/conditional-warning-popup-button/ConditionalWarningPopupButton';
import { Form } from 'components/organisms';
import { artistInitialValues, ArtistTabs } from 'components/organisms/forms/artist/ArtistForm';
import { listQueryKeys } from 'constants/queryKey';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useArtistGoBack } from 'hooks/useArtistGoBack';
import middleware from 'middleware';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostArtist } from 'services/artists';
import { useCreateProfilePicture } from 'services/artists/profilePicture';
import { IArtistMutationForm, IArtistResponse } from 'services/artists/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const CreateArtist = () => {
  const createArtist = usePostArtist();
  const createProfilePicture = useCreateProfilePicture();

  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const mutateOptions = useMutateOptions<IArtistMutationForm>();
  const { musicProductDigitalForm, trackForm } = useStoreState();

  const { onGoBack } = useArtistGoBack();

  const onSuccessCreatePicture = async (file: File, artist: IArtistResponse) => {
    await createProfilePicture.mutateAsync(
      {
        id: artist.id,
        file: file,
        version: artist.version,
      },
      {
        onSuccess: async (picture) => {
          onGoBack(artist.id, {
            ...artist,
            profilePictureLink: picture.pictureLink,
            profilePicturePreviewLink: picture.picturePreviewLink,
          });
          await queryClient.invalidateQueries({
            queryKey: [listQueryKeys.artists],
          });
        },
        onError: async (error) => {
          onErrorShowAllErrorsToast(error);
          onGoBack(artist.id, artist);
          await queryClient.invalidateQueries({
            queryKey: [listQueryKeys.artists],
          });
        },
      },
    );
  };

  const onCreateArtist = async (
    values: IArtistMutationForm,
    helpers: FormikHelpers<IArtistMutationForm>,
  ) => {
    const artist = middleware.artist.formToQuery(values);

    await createArtist.mutateAsync(artist, {
      ...mutateOptions(helpers),
      onSuccess: async (data) => {
        if (values.file) {
          return await onSuccessCreatePicture(values.file, data);
        }
        await queryClient.invalidateQueries({
          queryKey: [listQueryKeys.artists],
        });
        onGoBack(data.id, data);
      },
    });
  };

  return (
    <Form.Artist
      i18nextPrefix="artist"
      tittle={t('artist.title.create')}
      initialValues={artistInitialValues}
      onSubmit={onCreateArtist}
      customBackItem={
        <ConditionalWarningPopupButton
          title={t('popup.form-cancelation-title')}
          message={t('popup.form-cancelation-description')}
          confirmButtonVariant={BUTTON_VARIANT.background}
          showWarning={!musicProductDigitalForm && !trackForm}
          onConfirm={() => onGoBack()}
          isClear
          onlyIcon
        >
          <Button.Icon icon={<PiX />} />
        </ConditionalWarningPopupButton>
      }
      disableTabs={[ArtistTabs.bio]}
    />
  );
};

export default CreateArtist;
